import {
  Bell,
  BookOpenCheck,
  Building2,
  Clock3,
  Code2,
  ExternalLink,
  KeyRound,
  Loader2,
  MapPinned,
  RefreshCw,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { settingsApi } from "../../backend/api/settings";
import { useAdminSettings } from "../../backend/queries/settings";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

const icons: Record<string, LucideIcon> = {
  "api-contract": Code2,
  "organisation": Building2,
  "roles-access": ShieldCheck,
  "sign-in": KeyRound,
  locations: MapPinned,
  "pay-period": Clock3,
  xero: WalletCards,
  training: BookOpenCheck,
  notifications: Bell,
};

export default function AdminSettings() {
  const { data, error, isPending, refetch, isFetching } = useAdminSettings();

  return (
    <main className="mx-auto w-full max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          <ShieldCheck className="h-4 w-4" aria-hidden="true" />
          Admin only
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
          Platform configuration and integration status. Sensitive provider
          credentials and deployment secrets are never exposed here.
        </p>
      </header>

      {isPending ? (
        <div className="flex min-h-64 items-center justify-center rounded-lg border bg-card">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
          <span>Loading settings</span>
        </div>
      ) : error || !data ? (
        <Card role="alert">
          <CardHeader>
            <CardTitle>Settings are unavailable</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : "Please try again."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden border-primary/20">
            <CardHeader className="border-b bg-muted/40 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Code2 className="h-5 w-5 text-primary" aria-hidden="true" />
                  <CardTitle>API contract</CardTitle>
                  <Badge variant="secondary">Available</Badge>
                </div>
                <CardDescription>
                  The Rust API is the source of truth for documented routes and
                  generated frontend contracts.
                </CardDescription>
              </div>
              <Button asChild variant="outline" className="mt-3 sm:mt-0">
                <a
                  href={settingsApi.openApiUrl()}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open specification
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
            </CardHeader>
            <CardContent className="grid gap-4 pt-5 sm:grid-cols-2 lg:grid-cols-4">
              <ContractValue label="API version" value={data.api_contract.api_version} />
              <ContractValue label="OpenAPI" value={data.api_contract.openapi_version} />
              <ContractValue label="Documented operations" value={String(data.api_contract.route_count)} />
              <ContractValue label="Base path" value={data.api_base_path} />
              <div className="sm:col-span-2 lg:col-span-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Authentication
                </p>
                <p className="mt-1 text-sm font-medium">{data.authentication}</p>
              </div>
            </CardContent>
          </Card>

          <section aria-labelledby="platform-settings-heading" className="space-y-3">
            <div>
              <h2 id="platform-settings-heading" className="text-lg font-semibold">
                Platform areas
              </h2>
              <p className="text-sm text-muted-foreground">
                Current availability is reported by the API, so this page does
                not imply unsupported configuration.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {data.sections
                .filter((section) => section.key !== "api-contract")
                .map((section) => {
                  const Icon = icons[section.key] || Building2;
                  return (
                    <Card key={section.key}>
                      <CardHeader>
                        <div className="flex items-start justify-between gap-3">
                          <div className="rounded-md bg-muted p-2">
                            <Icon className="h-5 w-5" aria-hidden="true" />
                          </div>
                          <Badge variant="outline">
                            {section.availability === "available" ? "Available" : "Planned"}
                          </Badge>
                        </div>
                        <CardTitle className="pt-2">{section.label}</CardTitle>
                        <CardDescription className="leading-5">
                          {section.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  );
                })}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function ContractValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 break-words font-mono text-sm font-semibold">{value}</p>
    </div>
  );
}
